import { faArrowsRepeat } from '@fortawesome/pro-light-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ApiAccounts, ApiAccountsFormData } from '../types';

import { schema } from '../utils/schema';
import { convertFlagsObjectToArray } from 'utils/misc';

import { API_ACCOUNTS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';

import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledTextField
} from 'components/shared/ControlledFields';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { showErrorSnackbar } from 'components/shared/Snackbar';
import { FontAwesomeSvgIcon } from 'components/shared/index';

import { useGenerateAPIKey } from '../hooks/useGenerateAPIKey';

interface Props {
  handleClose: () => void;
  formData: ApiAccountsFormData;
  showAPIKey: (apiKey: string) => void;
  isOpenForGenerating: boolean;
}

const convertToPayload = (data: ApiAccountsFormData): ApiAccounts => {
  return { ...data, flags: convertFlagsObjectToArray(data.flags) };
};

export const EditOrCreateForm = ({
  formData,
  handleClose,
  showAPIKey,
  isOpenForGenerating
}: Props) => {
  const { t } = useTranslation('common');
  const { mutateAsync: save, isLoading } = useSaveResource({
    url: API_ACCOUNTS,
    id: formData?.accountId ?? null,
    invalidateQueryKey: ['apiAccounts', 'list']
  });
  const { mutateAsync: generateAPIKey, isLoading: isLoadingGenerate } =
    useGenerateAPIKey();

  const methods = useForm({
    defaultValues: schema.cast(formData),
    resolver: yupResolver(schema)
  });

  const { control, handleSubmit } = methods;

  const generate = (apiKey) => {
    generateAPIKey(apiKey)
      .then((res) => {
        handleClose();
        showAPIKey(res.data.apiKey);
      })
      .catch(showErrorSnackbar);
  };

  const onSubmit = (data) => {
    const payload = convertToPayload(data);
    save(payload).then((res) => {
      if (formData) {
        handleClose();
      } else {
        generate(res.data.accountId);
      }
    });
  };

  return (
    <>
      <MultiStepForm methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <MultiStepForm.Section title={t('basis')}>
          <ControlledTextField
            disabled={isOpenForGenerating}
            control={control}
            name="accountName"
            label={t('api_accounts.accountName')}
          />
        </MultiStepForm.Section>
        <MultiStepForm.Section title={t('validity')}>
          <ControlledDatePicker
            disabled={isOpenForGenerating}
            fullWidth
            control={control}
            name="valid.from"
            label={t('validFrom')}
          />
          <ControlledDatePicker
            disabled={isOpenForGenerating}
            fullWidth
            control={control}
            name="valid.to"
            label={t('validTo')}
          />
          <ControlledCheckbox
            disabled={isOpenForGenerating}
            control={control}
            name="flags.DISABLED"
            label={t('api_accounts.flags.DISABLED')}
          />
          <ControlledCheckbox
            disabled={isOpenForGenerating}
            control={control}
            name="flags.LOCKED"
            label={t('api_accounts.flags.LOCKED')}
          />
        </MultiStepForm.Section>
        <MultiStepForm.Actions>
          {isOpenForGenerating ? (
            <LoadingButton
              startIcon={<FontAwesomeSvgIcon icon={faArrowsRepeat} />}
              loading={isLoadingGenerate}
              variant="contained"
              autoFocus
              onClick={() => generate(formData.accountId)}
              color="dark"
            >
              {t('api_accounts.generate_key')}
            </LoadingButton>
          ) : (
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="dark"
            >
              {t('save')}
            </LoadingButton>
          )}
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};
